import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  FileText, Download, Plus, Search, BookOpen, Loader2,
  CheckCircle2, AlertTriangle, AlertCircle, Building2, Scale,
  Receipt, ChevronRight, ChevronDown, History,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useEmpDatabooks } from '@/hooks/useEmpDatabooks';
import { VirtualDocumentTable } from './arquivo/VirtualDocumentTable';
import { CreateDocumentDialog } from './arquivo/CreateDocumentDialog';
import { ConcurrencyBanner } from './arquivo/ConcurrencyBanner';

// ─── Constants ──────────────────────────────────────────────────────────────

interface FamiliaDef {
  id: string;
  label: string;
  icon: React.ElementType;
  tipos: { value: string; label: string }[];
}

const FAMILIAS: FamiliaDef[] = [
  {
    id: 'contratual',
    label: 'Documentos Contratuais',
    icon: Building2,
    tipos: [
      { value: 'contratual_proposta', label: 'Proposta / Edital' },
      { value: 'contratual_contrato', label: 'Contrato Principal' },
      { value: 'contratual_aditivo', label: 'Aditivo / Termo' },
      { value: 'contratual_ata', label: 'Ata de Reunião' },
      { value: 'contratual_notificacao', label: 'Notificação' },
      { value: 'contratual_outro', label: 'Outro' },
    ],
  },
  {
    id: 'tecnico',
    label: 'Documentos Técnicos',
    icon: FileText,
    tipos: [
      { value: 'tecnico_projeto', label: 'Projeto / Planta' },
      { value: 'tecnico_art', label: 'ART / RRT' },
      { value: 'tecnico_laudo', label: 'Laudo / Ensaio' },
      { value: 'tecnico_memorial', label: 'Memorial Descritivo' },
      { value: 'tecnico_relatorio', label: 'Relatório Técnico' },
      { value: 'tecnico_registro_execucao', label: 'Registro de Execução' },
      { value: 'tecnico_evidencia', label: 'Evidência de Campo' },
      { value: 'tecnico_outro', label: 'Outro' },
    ],
  },
  {
    id: 'financeiro',
    label: 'Documentos Financeiros',
    icon: Receipt,
    tipos: [
      { value: 'financeiro_boletim', label: 'Boletim de Medição' },
      { value: 'financeiro_planilha', label: 'Planilha Orçamentária' },
      { value: 'financeiro_nota_fiscal', label: 'Nota Fiscal' },
      { value: 'financeiro_medicao', label: 'Medição' },
      { value: 'financeiro_outro', label: 'Outro' },
    ],
  },
  {
    id: 'legal',
    label: 'Documentos Legais / Compliance',
    icon: Scale,
    tipos: [
      { value: 'legal_certificado', label: 'Certificado' },
      { value: 'legal_alvara', label: 'Alvará' },
      { value: 'legal_licenca', label: 'Licença' },
      { value: 'legal_seguro', label: 'Seguro' },
      { value: 'legal_outro', label: 'Outro' },
    ],
  },
  {
    id: 'databook',
    label: 'Databooks',
    icon: BookOpen,
    tipos: [],
  },
];

const ALL_TIPOS = FAMILIAS.flatMap(f => f.tipos);

// ─── Types ──────────────────────────────────────────────────────────────────

interface EmpDocumento {
  id: string;
  contrato_id: string;
  titulo: string;
  tipo_documental: string;
  familia: string;
  origem: string;
  codigo_documento: string | null;
  descricao: string | null;
  ativo: boolean;
  created_at: string;
}

interface EmpDocumentoVersao {
  id: string;
  documento_id: string;
  numero_versao: number;
  storage_path: string;
  nome_arquivo: string;
  tamanho_bytes: number | null;
  mime_type: string | null;
  created_at: string;
}

const formatBytes = (bytes: number | null) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (d: string | null) => {
  if (!d) return '—';
  try { return format(new Date(d), 'dd/MM/yyyy', { locale: ptBR }); } catch { return d; }
};

const DATABOOK_STATUS: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive'; icon: React.ElementType }> = {
  processing: { label: 'Gerando', variant: 'secondary', icon: Loader2 },
  ready: { label: 'Pronto', variant: 'default', icon: CheckCircle2 },
  failed: { label: 'Falha', variant: 'destructive', icon: AlertTriangle },
  superseded: { label: 'Superado', variant: 'secondary', icon: History },
};

// ─── Component ──────────────────────────────────────────────────────────────

interface EmpArquivoViewProps {
  contratoId: string;
  contratoNome: string;
  canEdit: boolean;
}

export const EmpArquivoView: React.FC<EmpArquivoViewProps> = ({
  contratoId, contratoNome, canEdit,
}) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // ── Audit helper ─────────────────────────────────────────────────────
  const logAuditoria = useCallback(async (acao: string, entidade: string, entidadeId: string | null, dados: Record<string, unknown>) => {
    try {
      await (supabase as any).from('auditoria_eventos').insert({
        acao, entidade, entidade_id: entidadeId,
        user_id: user?.id || null, dados_resumo: dados,
      });
    } catch (err) {
      console.warn('Falha ao registrar auditoria:', err);
    }
  }, [user?.id]);

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [versionsDocId, setVersionsDocId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [expandedFamilias, setExpandedFamilias] = useState<Set<string>>(
    new Set(FAMILIAS.map(f => f.id))
  );
  const [downloading, setDownloading] = useState<string | null>(null);
  const [pdfPreview, setPdfPreview] = useState<{
    open: boolean;
    url: string | null;
    fileName: string;
    revoke?: () => void;
  }>({ open: false, url: null, fileName: '' });

  const openPdfPreview = useCallback((url: string, fileName: string, revoke?: () => void) => {
    setPdfPreview(prev => {
      if (prev.revoke) prev.revoke();
      return { open: true, url, fileName, revoke };
    });
  }, []);

  const closePdfPreview = useCallback(() => {
    setPdfPreview(prev => {
      if (prev.revoke) prev.revoke();
      return { open: false, url: null, fileName: '', revoke: undefined };
    });
  }, []);

  // ── Queries ─────────────────────────────────────────────────────────────

  const { data: documentos = [], isLoading } = useQuery({
    queryKey: ['emp-documentos', contratoId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('emp_documentos')
        .select('*')
        .eq('contrato_id', contratoId)
        .eq('ativo', true)
        .order('familia', { ascending: true })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as EmpDocumento[];
    },
    enabled: !!contratoId,
    // Auto-refresh while any document is pending materialization
    refetchInterval: (query) => {
      const docs = query.state.data as EmpDocumento[] | undefined;
      const hasPending = docs?.some(d => (d as any).status_materializacao === 'pendente' || (d as any).status_materializacao === 'processando');
      return hasPending ? 5000 : false;
    },
  });

  const { data: versoes = [] } = useQuery({
    queryKey: ['emp-documento-versoes', versionsDocId],
    queryFn: async () => {
      if (!versionsDocId) return [];
      const { data, error } = await (supabase as any)
        .from('emp_documento_versoes')
        .select('*')
        .eq('documento_id', versionsDocId)
        .order('numero_versao', { ascending: false });
      if (error) throw error;
      return (data || []) as EmpDocumentoVersao[];
    },
    enabled: !!versionsDocId,
  });

  const { data: latestVersions = {} } = useQuery({
    queryKey: ['emp-documento-latest-versions', contratoId],
    queryFn: async () => {
      // Join through emp_documentos to filter by contrato_id
      const { data, error } = await (supabase as any)
        .from('emp_documento_versoes')
        .select('documento_id, numero_versao, emp_documentos!inner(contrato_id)')
        .eq('emp_documentos.contrato_id', contratoId)
        .order('numero_versao', { ascending: false });
      if (error) throw error;
      const map: Record<string, number> = {};
      for (const v of (data || [])) {
        if (!map[v.documento_id]) map[v.documento_id] = v.numero_versao;
      }
      return map;
    },
    enabled: !!contratoId,
    // Sync with materialization
    refetchInterval: (query) => {
      const hasPending = documentos.some(d => (d as any).status_materializacao === 'pendente' || (d as any).status_materializacao === 'processando');
      return hasPending ? 5000 : false;
    },
  });

  // Databooks
  const {
    databooks, isLoading: databooksLoading, hasProcessing,
    generating, gerarDatabook, getDownloadUrl,
  } = useEmpDatabooks(contratoId);

  // ── Mutations ───────────────────────────────────────────────────────────

  const createDocMutation = useMutation({
    mutationFn: async (formData: { titulo: string; familia: string; tipo: string; descricao: string; file: File }) => {
      setUploading(true);
      const { data: doc, error: docError } = await (supabase as any)
        .from('emp_documentos')
        .insert({
          contrato_id: contratoId,
          titulo: formData.titulo.trim(),
          tipo_documental: formData.tipo,
          familia: formData.familia,
          origem: 'manual',
          descricao: formData.descricao.trim() || null,
        })
        .select('id')
        .single();
      if (docError) throw docError;

      const path = `${contratoId}/${doc.id}/v1_${formData.file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('emp-documentos')
        .upload(path, formData.file, { upsert: false });
      if (uploadError) throw uploadError;

      const { error: verError } = await (supabase as any)
        .from('emp_documento_versoes')
        .insert({
          documento_id: doc.id,
          numero_versao: 1,
          storage_path: path,
          nome_arquivo: formData.file.name,
          tamanho_bytes: formData.file.size,
          mime_type: formData.file.type || 'application/octet-stream',
        });
      if (verError) throw verError;

      return { docId: doc.id, titulo: formData.titulo, file: formData.file };
    },
    onSuccess: (_data) => {
      queryClient.invalidateQueries({ queryKey: ['emp-documentos', contratoId] });
      queryClient.invalidateQueries({ queryKey: ['emp-documento-latest-versions', contratoId] });
      toast({ title: 'Documento criado com sucesso.' });
      logAuditoria('documento_criado', 'emp_documentos', contratoId, {
        titulo: _data.titulo, arquivo: _data.file.name,
      });
      setCreateOpen(false);
    },
    onError: (err: Error) => {
      toast({ title: 'Erro ao criar documento', description: err.message, variant: 'destructive' });
    },
    onSettled: () => setUploading(false),
  });

  const uploadVersionMutation = useMutation({
    mutationFn: async ({ docId, file }: { docId: string; file: File }) => {
      const { data: existing, error: fetchErr } = await (supabase as any)
        .from('emp_documento_versoes')
        .select('numero_versao')
        .eq('documento_id', docId)
        .order('numero_versao', { ascending: false })
        .limit(1);
      if (fetchErr) throw fetchErr;
      const nextVersion = (existing?.[0]?.numero_versao ?? 0) + 1;

      const path = `${contratoId}/${docId}/v${nextVersion}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('emp-documentos')
        .upload(path, file, { upsert: false });
      if (uploadError) throw uploadError;

      const { error: verError } = await (supabase as any)
        .from('emp_documento_versoes')
        .insert({
          documento_id: docId,
          numero_versao: nextVersion,
          storage_path: path,
          nome_arquivo: file.name,
          tamanho_bytes: file.size,
          mime_type: file.type || 'application/octet-stream',
        });
      if (verError) throw verError;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['emp-documentos', contratoId] });
      queryClient.invalidateQueries({ queryKey: ['emp-documento-latest-versions', contratoId] });
      queryClient.invalidateQueries({ queryKey: ['emp-documento-versoes', versionsDocId] });
      toast({ title: 'Nova versão carregada.' });
      logAuditoria('versao_upload', 'emp_documento_versoes', variables.docId, {
        arquivo: variables.file.name, tamanho: variables.file.size,
      });
    },
    onError: (err: Error) => {
      toast({ title: 'Erro ao carregar versão', description: err.message, variant: 'destructive' });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async (docId: string) => {
      const { error } = await (supabase as any)
        .from('emp_documentos')
        .update({ ativo: false })
        .eq('id', docId);
      if (error) throw error;
    },
    onSuccess: (_data, docId) => {
      queryClient.invalidateQueries({ queryKey: ['emp-documentos', contratoId] });
      toast({ title: 'Documento arquivado.' });
      logAuditoria('documento_arquivado', 'emp_documentos', docId, { contrato_id: contratoId });
    },
    onError: (err: Error) => {
      toast({ title: 'Erro ao arquivar', description: err.message, variant: 'destructive' });
    },
  });

  // ── Handlers ────────────────────────────────────────────────────────────

  const PDF_MIME_TYPES = ['application/pdf'];

  const getLatestVersionForDoc = useCallback(async (docId: string) => {
    const { data, error } = await (supabase as any)
      .from('emp_documento_versoes')
      .select('storage_path, mime_type, nome_arquivo')
      .eq('documento_id', docId)
      .order('numero_versao', { ascending: false })
      .limit(1)
      .single();
    if (error || !data) return null;
    return data as { storage_path: string; mime_type: string | null; nome_arquivo: string };
  }, []);

  const getDocumentAccess = useCallback(async (
    storagePath: string,
    options?: { expiresIn?: number; preferBlob?: boolean },
  ) => {
    const expiresIn = options?.expiresIn ?? 60;
    const preferBlob = options?.preferBlob ?? false;

    let signedUrl: string | null = null;

    const { data: fnData, error: fnError } = await supabase.functions.invoke('arquivo-get-download-url', {
      body: {
        bucket: 'emp-documentos',
        storage_path: storagePath,
        expires_in: expiresIn,
      },
    });

    if (!fnError && fnData?.signed_url) {
      signedUrl = fnData.signed_url as string;
    }

    if (!signedUrl) {
      const signed = await supabase.storage
        .from('emp-documentos')
        .createSignedUrl(storagePath, expiresIn);

      if (!signed.error && signed.data?.signedUrl) {
        signedUrl = signed.data.signedUrl;
      }
    }

    if (signedUrl && !preferBlob) {
      return { url: signedUrl, revoke: undefined as (() => void) | undefined };
    }

    if (signedUrl && preferBlob) {
      try {
        const response = await fetch(signedUrl);
        if (response.ok) {
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          return {
            url: objectUrl,
            revoke: () => URL.revokeObjectURL(objectUrl),
          };
        }
      } catch (error) {
        console.warn('Falha ao converter URL assinada em blob local:', error);
      }
    }

    // Fallback final: download direto + Object URL temporária
    const downloaded = await supabase.storage
      .from('emp-documentos')
      .download(storagePath);

    if (downloaded.error || !downloaded.data) {
      console.error('Storage access error:', fnError || downloaded.error);
      return null;
    }

    const objectUrl = URL.createObjectURL(downloaded.data);
    return {
      url: objectUrl,
      revoke: () => URL.revokeObjectURL(objectUrl),
    };
  }, []);

  const handleViewDocument = useCallback(async (docId: string) => {
    const version = await getLatestVersionForDoc(docId);
    if (!version) {
      toast({ title: 'Documento sem versão', description: 'Nenhuma versão ativa encontrada para este documento.', variant: 'destructive' });
      return;
    }
    if (!version.storage_path) {
      toast({ title: 'Arquivo indisponível', description: 'O caminho do arquivo não foi encontrado.', variant: 'destructive' });
      return;
    }

    const access = await getDocumentAccess(version.storage_path, { expiresIn: 60, preferBlob: true });
    if (!access) {
      toast({ title: 'Erro ao gerar link', description: 'Não foi possível gerar a URL de visualização. Tente novamente.', variant: 'destructive' });
      return;
    }

    const isPdf = PDF_MIME_TYPES.includes(version.mime_type || '') ||
      version.nome_arquivo?.toLowerCase().endsWith('.pdf');

    if (isPdf) {
      openPdfPreview(access.url, version.nome_arquivo || 'documento.pdf', access.revoke);
      return;
    }

    // Force download for non-PDF files
    const a = document.createElement('a');
    a.href = access.url;
    a.download = version.nome_arquivo || 'documento';
    a.click();
    toast({ title: 'Download iniciado', description: 'Arquivo não é PDF — download automático.' });

    if (access.revoke) window.setTimeout(access.revoke, 60_000);
  }, [getLatestVersionForDoc, getDocumentAccess, openPdfPreview, toast]);

  const handleDownloadDocument = useCallback(async (docId: string) => {
    const version = await getLatestVersionForDoc(docId);
    if (!version) {
      toast({ title: 'Documento sem versão', description: 'Nenhuma versão ativa encontrada para este documento.', variant: 'destructive' });
      return;
    }
    if (!version.storage_path) {
      toast({ title: 'Arquivo indisponível', description: 'O caminho do arquivo não foi encontrado.', variant: 'destructive' });
      return;
    }

    const access = await getDocumentAccess(version.storage_path, { expiresIn: 60, preferBlob: true });
    if (!access) {
      toast({ title: 'Erro ao gerar link', description: 'Não foi possível gerar a URL de download. Tente novamente.', variant: 'destructive' });
      return;
    }

    const a = document.createElement('a');
    a.href = access.url;
    a.download = version.nome_arquivo || 'documento';
    a.click();

    if (access.revoke) window.setTimeout(access.revoke, 60_000);
  }, [getLatestVersionForDoc, getDocumentAccess, toast]);

  const handleDownloadFile = async (storagePath: string) => {
    const access = await getDocumentAccess(storagePath, { expiresIn: 3600, preferBlob: true });
    if (!access) {
      toast({ title: 'Erro ao gerar link de download', variant: 'destructive' });
      return;
    }

    const guessedName = storagePath.split('/').pop() || 'arquivo';
    const a = document.createElement('a');
    a.href = access.url;
    a.download = guessedName;
    a.click();

    if (access.revoke) window.setTimeout(access.revoke, 60_000);
  };


  const handleDownloadDatabook = useCallback(async (databookId: string, storagePath: string, titulo: string) => {
    setDownloading(databookId);
    try {
      const url = await getDownloadUrl(storagePath);
      if (url) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${titulo}.pdf`;
        a.click();
      }
    } finally {
      setDownloading(null);
    }
  }, [getDownloadUrl]);

  const handleGerarDatabook = useCallback(async () => {
    const nonDatabookDocs = documentos.filter(d => d.familia !== 'databook');
    if (nonDatabookDocs.length === 0) {
      toast({ title: 'Nenhum documento disponível', description: 'Adicione documentos antes de gerar o Databook.', variant: 'destructive' });
      return;
    }
    const ids = nonDatabookDocs.map(d => d.id);
    const result = await gerarDatabook(ids, `Databook — ${contratoNome}`);
    if (result) {
      logAuditoria('databook_gerado', 'emp_databooks', result.databook_id || contratoId, {
        total_documentos: ids.length, titulo: `Databook — ${contratoNome}`,
      });
    }
  }, [documentos, gerarDatabook, contratoNome, toast, logAuditoria, contratoId]);

  const handleUploadVersion = useCallback((docId: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = (e: any) => {
      const file = e.target?.files?.[0];
      if (file) uploadVersionMutation.mutate({ docId, file });
    };
    input.click();
  }, [uploadVersionMutation]);

  const toggleFamilia = (familiaId: string) => {
    setExpandedFamilias(prev => {
      const next = new Set(prev);
      if (next.has(familiaId)) next.delete(familiaId);
      else next.add(familiaId);
      return next;
    });
  };

  const tipoLabel = (tipo: string) =>
    ALL_TIPOS.find(o => o.value === tipo)?.label || tipo;

  // ── Derived data ────────────────────────────────────────────────────────

  const filtered = documentos.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return d.titulo.toLowerCase().includes(q) || d.codigo_documento?.toLowerCase().includes(q);
  });

  const groupedByFamilia = FAMILIAS.map(fam => ({
    ...fam,
    docs: filtered.filter(d => d.familia === fam.id),
  }));

  const nonDatabookCount = documentos.filter(d => d.familia !== 'databook').length;

  // Lock tracking (docs being uploaded/archived currently)
  const lockedDocIds = new Set<string>();
  if (uploadVersionMutation.isPending && uploadVersionMutation.variables) {
    lockedDocIds.add(uploadVersionMutation.variables.docId);
  }
  if (archiveMutation.isPending && archiveMutation.variables) {
    lockedDocIds.add(archiveMutation.variables);
  }

  // ── Render ──────────────────────────────────────────────────────────────

  if (isLoading) {
    return <div className="space-y-3"><Skeleton className="h-10 w-full" /><Skeleton className="h-48 w-full" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Concurrency Banner */}
      <ConcurrencyBanner
        lockedCount={lockedDocIds.size}
        databookGenerating={generating || hasProcessing}
        uploadInProgress={uploading}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por título ou código..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button onClick={() => setCreateOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-1" /> Novo Documento
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleGerarDatabook}
            disabled={generating || hasProcessing || nonDatabookCount === 0}
            className="gap-1.5"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />}
            Gerar Databook
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span><strong className="text-foreground">{documentos.length.toLocaleString()}</strong> documentos</span>
        <span>·</span>
        <span><strong className="text-foreground">{databooks.filter(d => d.status === 'ready').length}</strong> databooks prontos</span>
        {hasProcessing && (
          <span className="flex items-center gap-1 text-warning">
            <Loader2 className="h-3 w-3 animate-spin" /> Gerando databook...
          </span>
        )}
      </div>

      {/* ── Tree View ─────────────────────────────────────────────────────── */}
      <div className="space-y-2">
        {groupedByFamilia.map(fam => {
          const isDatabook = fam.id === 'databook';
          const isExpanded = expandedFamilias.has(fam.id);
          const FamIcon = fam.icon;
          const count = isDatabook ? databooks.length : fam.docs.length;

          return (
            <Card key={fam.id} className="overflow-hidden">
              <Collapsible open={isExpanded} onOpenChange={() => toggleFamilia(fam.id)}>
                <CollapsibleTrigger asChild>
                  <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left">
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                    <FamIcon className="h-4 w-4 text-emp-primary" strokeWidth={1.5} />
                    <span className="font-medium text-sm flex-1">{fam.label}</span>
                    <Badge variant="secondary" className="text-xs">{count}</Badge>
                  </button>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  {isDatabook ? (
                    /* Databook section */
                    <div className="border-t">
                      {databooks.length === 0 ? (
                        <div className="px-4 py-6 text-center text-muted-foreground text-sm">
                          <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-40" />
                          <p>Nenhum Databook gerado.</p>
                          <p className="text-xs mt-1">Clique em <strong>Gerar Databook</strong> para compilar documentos.</p>
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Título</TableHead>
                              <TableHead>Versão</TableHead>
                              <TableHead>Volume</TableHead>
                              <TableHead>Docs</TableHead>
                              <TableHead>Tamanho</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Data</TableHead>
                              <TableHead className="w-[60px]" />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {databooks.map(db => {
                              const st = DATABOOK_STATUS[db.status] || DATABOOK_STATUS.ready;
                              const StIcon = st.icon;
                              const hasErrors = db.erros_parciais && db.erros_parciais.length > 0;

                              return (
                                <TableRow key={db.id}>
                                  <TableCell className="font-medium">
                                    <div className="flex items-center gap-1.5">
                                      {db.titulo}
                                      {hasErrors && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger>
                                              <AlertCircle className="h-3.5 w-3.5 text-warning" />
                                            </TooltipTrigger>
                                            <TooltipContent side="right" className="max-w-xs">
                                              <p className="font-medium mb-1">{db.erros_parciais.length} erro(s):</p>
                                              {db.erros_parciais.slice(0, 5).map((e: any, i: number) => (
                                                <p key={i} className="text-xs">{e.titulo}: {e.erro}</p>
                                              ))}
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className="text-xs">v{(db as any).versao_numero || 1}</Badge>
                                  </TableCell>
                                  <TableCell>
                                    {db.total_volumes > 1 ? `${db.volume_numero}/${db.total_volumes}` : '—'}
                                  </TableCell>
                                  <TableCell>{db.total_documentos}</TableCell>
                                  <TableCell>{formatBytes(db.tamanho_bytes)}</TableCell>
                                  <TableCell>
                                    <div className="flex flex-col gap-1">
                                      <Badge variant={st.variant} className="flex items-center gap-1 w-fit">
                                        <StIcon className={`h-3 w-3 ${db.status === 'processing' ? 'animate-spin' : ''}`} /> {st.label}
                                      </Badge>
                                      {db.status === 'processing' && db.progresso_percentual > 0 && (
                                        <Progress value={db.progresso_percentual} className="h-1.5 w-20" />
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-xs text-muted-foreground">{fmtDate(db.created_at)}</TableCell>
                                  <TableCell>
                                    {db.status === 'ready' && db.storage_path && (
                                      <Button
                                        variant="ghost" size="sm"
                                        disabled={downloading === db.id}
                                        onClick={() => handleDownloadDatabook(db.id, db.storage_path!, db.titulo)}
                                      >
                                        {downloading === db.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  ) : (
                    /* Regular document family — uses VirtualDocumentTable */
                    <div className="border-t">
                      {fam.docs.length === 0 ? (
                        <div className="px-4 py-4 text-center text-muted-foreground text-sm">
                          Nenhum documento nesta categoria.
                        </div>
                      ) : (
                        <VirtualDocumentTable
                          docs={fam.docs}
                          latestVersions={latestVersions}
                          tipoLabel={tipoLabel}
                          canEdit={canEdit}
                          lockedDocIds={lockedDocIds}
                          onViewDocument={handleViewDocument}
                          onDownloadDocument={handleDownloadDocument}
                          onViewVersions={setVersionsDocId}
                          onUploadVersion={handleUploadVersion}
                          onArchive={(docId) => archiveMutation.mutate(docId)}
                        />
                      )}
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
      </div>

      {/* ── Create Document Dialog (with zod validation + duplicate check) ── */}
      <CreateDocumentDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        contratoId={contratoId}
        familias={FAMILIAS}
        uploading={uploading}
        onSubmit={(data) => createDocMutation.mutate(data)}
      />

      {/* ── PDF Preview Dialog ─────────────────────────────────────────────── */}
      <Dialog
        open={pdfPreview.open}
        onOpenChange={(open) => {
          if (!open) closePdfPreview();
        }}
      >
        <DialogContent className="w-[95vw] max-w-6xl h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="px-4 py-3 border-b">
            <DialogTitle className="text-sm truncate">{pdfPreview.fileName || 'Visualização de PDF'}</DialogTitle>
          </DialogHeader>
          <div className="h-full bg-muted/20">
            {pdfPreview.url ? (
              <iframe
                src={pdfPreview.url}
                title={pdfPreview.fileName || 'Visualização de PDF'}
                className="w-full h-full border-0"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                Sem arquivo para visualização.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Versions Dialog ────────────────────────────────────────────────── */}
      <Dialog open={!!versionsDocId} onOpenChange={() => setVersionsDocId(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Histórico de Versões</DialogTitle>
          </DialogHeader>
          {versoes.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Nenhuma versão encontrada.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Versão</TableHead>
                  <TableHead>Arquivo</TableHead>
                  <TableHead>Tamanho</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {versoes.map(v => (
                  <TableRow key={v.id}>
                    <TableCell><Badge variant="secondary">v{v.numero_versao}</Badge></TableCell>
                    <TableCell className="text-sm truncate max-w-[150px]">{v.nome_arquivo}</TableCell>
                    <TableCell className="text-sm">{formatBytes(v.tamanho_bytes)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(v.created_at)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => handleDownloadFile(v.storage_path)}>
                        <Download className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
